import { faPlus, faSearch } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';

import type { Template } from '@feathr/blackbox';
import { TemplateClass, templateTypeLabel } from '@feathr/blackbox';
import type { ISelectOption } from '@feathr/components';
import {
  Button,
  DebouncedInput,
  Form,
  Input,
  Modal,
  Select,
  showError,
  Table,
  Toolbar,
} from '@feathr/components';
import Page from '@feathr/extender/App/Page';
import { templatesBlankToClassMap } from '@feathr/extender/components/TemplateSelect';
import TemplateTypeSelect from '@feathr/extender/components/TemplateTypeSelect';
import { useAccount, useLocalUrl, useStore } from '@feathr/extender/state';
import { useToggle } from '@feathr/hooks';
import type { IObject } from '@feathr/rachis';

import accountTemplatesColumns from './accountTemplatesColumns';

import * as styles from './AccountTemplatesPage.css';

import blankTemplate from '@feathr/extender/components/TemplateSelect/blankTemplate.json';

interface IFilters {
  name__icontains?: string;
  _cls?: TemplateClass;
  _cls__nin: TemplateClass[];
}

function AccountTemplatesPage(): JSX.Element {
  const account = useAccount();
  const { Templates } = useStore();
  const history = useHistory();
  const localUrl = useLocalUrl();
  const [filters, setFilters] = useState<IFilters>({
    _cls__nin: account?.isFinchLegacy
      ? [TemplateClass.Link]
      : [
          TemplateClass.Link,
          TemplateClass.ReferralPage,
          TemplateClass.ReferralEmail,
          TemplateClass.ReferralBanner,
        ],
  });
  const [name, setName] = useState<string | undefined>();
  const [templateType, setTemplateType] = useState<ISelectOption>({
    id: TemplateClass.SESEmail,
    name: templateTypeLabel(TemplateClass.SESEmail),
  });
  const [isModalVisible, toggleModalVisible] = useToggle(false);
  const { t } = useTranslation();

  function handleDebouncedSearchChange(newValue?: string): void {
    setFilters({
      ...filters,
      name__icontains: newValue,
    });
  }

  function onChangeType(id?: TemplateClass): void {
    setFilters({
      ...filters,
      _cls: id,
    });
  }

  function handleNameChange(newValue?: string): void {
    setName(newValue);
  }

  function handleTemplateTypeChange(newValue?: ISelectOption): void {
    setTemplateType(
      newValue || {
        id: TemplateClass.SESEmail,
        name: templateTypeLabel(TemplateClass.SESEmail),
      },
    );
  }

  async function handleAdd(): Promise<void> {
    toggleModalVisible();
    const templateClass = templateType.id as TemplateClass;
    const blankIndex = templatesBlankToClassMap[templateClass];
    const template = Templates.create({
      ...blankTemplate[blankIndex],
      id: undefined,
      _cls: templateClass,
      account: account.id,
      default: false,
      name,
      parent_kind: 'account',
      parent: account.id,
    });

    try {
      const response = await Templates.add(template, { validate: false });
      history.push(localUrl(response.getItemUrl('edit/design')));
    } catch (error) {
      showError(error, t);
    }
  }

  const filterElements = [
    <>
      <TemplateTypeSelect
        onChange={onChangeType}
        value={filters._cls}
        wrapperClassName={styles.filter}
      />
    </>,
    <>
      <DebouncedInput<string>
        defaultValue={filters.name__icontains}
        onChange={handleDebouncedSearchChange}
      >
        {(liveValue, onChangeLiveValue) => (
          <Input
            isClearable={true}
            onChange={onChangeLiveValue}
            placeholder={'Search by name...'}
            prefix={<FontAwesomeIcon icon={faSearch} />}
            type={'text'}
            value={liveValue}
          />
        )}
      </DebouncedInput>
    </>,
  ];

  // Filter to show only account-level templates
  const rawFilter: IObject = {
    __raw__: {
      is_archived: false,
      parent_kind: 'account',
      parent: `ObjectId('${account.id}')`,
      default: { $ne: true },
    },
  };

  if (filters._cls) {
    rawFilter.__raw__._cls = filters._cls;
  } else if (filters._cls__nin) {
    rawFilter.__raw__._cls = { $nin: filters._cls__nin };
  }

  if (filters.name__icontains) {
    rawFilter.__raw__.name = { $regex: filters.name__icontains, $options: 'i' };
  }

  const actions = (
    <Toolbar>
      <Button
        onClick={toggleModalVisible}
        prefix={<FontAwesomeIcon icon={faPlus} />}
        type={'primary'}
      >
        {t('Add template')}
      </Button>
    </Toolbar>
  );

  const typeOptions: ISelectOption[] = [
    {
      id: TemplateClass.SESEmail,
      name: templateTypeLabel(TemplateClass.SESEmail),
    },
    {
      id: TemplateClass.ReferralEmail,
      name: templateTypeLabel(TemplateClass.ReferralEmail),
    },
    {
      id: TemplateClass.ReferralBanner,
      name: templateTypeLabel(TemplateClass.ReferralBanner),
    },
    {
      id: TemplateClass.ReferralPage,
      name: templateTypeLabel(TemplateClass.ReferralPage),
    },
    {
      id: TemplateClass.LandingPage,
      name: templateTypeLabel(TemplateClass.LandingPage),
    },
  ];

  return (
    <Page actions={actions} title={t('Templates')}>
      <Table<Template>
        collection={Templates}
        columns={accountTemplatesColumns(t)}
        filterElements={filterElements}
        filters={rawFilter}
        initialSort={[{ id: 'date_last_modified', desc: true }]}
      />
      {isModalVisible && (
        <Modal
          allowOverflow={true}
          onClose={toggleModalVisible}
          opened={isModalVisible}
          title={t('Add New Template')}
        >
          <Form label={t('Add New Template')}>
            <Input
              helpText={t(
                'This is the name of the template used in Feathr and in your reports. Make it descriptive and memorable so you can find this template later.',
              )}
              label={t('Name')}
              onChange={handleNameChange}
              placeholder={t('Unnamed Template')}
              type={'text'}
              value={name}
            />
            <Select
              label={t('Type')}
              name={'template-type'}
              onChange={handleTemplateTypeChange}
              options={typeOptions}
              value={templateType}
            />
          </Form>
          <Toolbar>
            <Button onClick={toggleModalVisible} type={'secondary'}>
              {t('Cancel')}
            </Button>
            <Button disabled={!name || !templateType.id} onClick={handleAdd} type={'primary'}>
              {t('Create template')}
            </Button>
          </Toolbar>
        </Modal>
      )}
    </Page>
  );
}

export default AccountTemplatesPage;
